import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cityState: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('cityState', formData.cityState);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d62cvhs3ij519rauvnpg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', cityState: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section id="contact" className="bg-[#1A2B4A] py-16 sm:py-20 md:py-24 lg:py-28 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
          <h2 className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-4 sm:mb-6 lg:mb-8 px-4">
            <div>Ready to Make Your</div>
            <div>Home Safer Today?</div>
          </h2>
          <p className="text-white text-lg sm:text-xl md:text-2xl font-light px-4">
            Free consultation and home safety assessment - no obligation
          </p>
        </div>

        <div className="max-w-3xl mx-auto bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-2xl">
          <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              <div>
                <label htmlFor="name" className="block text-[#1A2B4A] font-semibold mb-2 text-sm sm:text-base">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm sm:text-base"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-[#1A2B4A] font-semibold mb-2 text-sm sm:text-base">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm sm:text-base"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              <div>
                <label htmlFor="phone" className="block text-[#1A2B4A] font-semibold mb-2 text-sm sm:text-base">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm sm:text-base"
                  placeholder="(978) 555-0123"
                />
              </div>
              <div>
                <label htmlFor="cityState" className="block text-[#1A2B4A] font-semibold mb-2 text-sm sm:text-base">
                  City, State *
                </label>
                <input
                  type="text"
                  id="cityState"
                  name="cityState"
                  required
                  value={formData.cityState}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm sm:text-base"
                  placeholder="Boston, MA"
                />
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-[#1A2B4A] font-semibold mb-2 text-sm sm:text-base">
                Tell Us About Your Needs
              </label>
              <textarea
                id="message"
                name="message"
                rows={4}
                maxLength={500}
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#D4AF37] focus:outline-none text-sm sm:text-base resize-none"
                placeholder="Please describe your accessibility needs or any specific concerns..."
              ></textarea>
              <div className="text-right text-xs sm:text-sm text-gray-500 mt-1">
                {formData.message.length}/500 characters
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#D4AF37] hover:bg-[#C4A137] text-[#1A2B4A] font-bold text-base sm:text-lg py-3 sm:py-4 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? 'Sending...' : 'Request Free Assessment'}
            </button>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border-2 border-green-500 text-green-700 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-center text-sm sm:text-base">
                <strong>Thank you!</strong> We'll contact you within 24 hours to schedule your free assessment.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border-2 border-red-500 text-red-700 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-center text-sm sm:text-base">
                <strong>Oops!</strong> Something went wrong. Please call us at (617) 539-6650.
              </div>
            )}
          </form>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 mt-8 sm:mt-10 lg:mt-12">
          <a
            href="tel:6175396650"
            className="w-full sm:w-auto group bg-transparent border-2 border-white hover:bg-white text-white hover:text-[#1A2B4A] px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-3"
          >
            <i className="ri-phone-line text-lg sm:text-xl"></i>
            Call (617) 539-6650
          </a>
          <button
            onClick={scrollToTop}
            className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer"
            aria-label="Scroll to top"
          >
            <i className="ri-arrow-up-line text-white text-xl"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
